import { create } from "zustand";
import { useRequestStore, GetAccessTokenFn } from "../BackendStore";
import {
  AutoCollectDetail,
  CheckoutDetailsView,
  CollectNowResponse,
  Invoice,
  InvoiceForClientPortalView,
  InvoicePartialPaymentInfoView,
  InvoicePaymentView,
  PaymentLinkCheckoutDetailView,
  Receipt
} from "../../Contracts/Invoice";
import { MultiGetResponse, ViewGeneratorKey } from "../../Contracts/Common";
import { useClientPortalMetadataStore } from "../ClientPortalMetadata";
import { useBannerStore } from "../Banner";
import { BannerType } from "../../Contracts/Banner";

const invoicesUrl = "/v1/payments/invoices";
const invoiceForClientPortalUrl = "/v1/payments/client-portal/invoices";
const invoicePdfViewUrl = "/v1/payments/customer/{customer_id}/invoice/{invoice_id}/viewPdf";
const getCheckoutDetailsUrl =
  "/v1/payments/customer/{customer_id}/invoice/{invoice_id}/checkoutDetail";
const getPaymentLinkCheckoutDetailsUrl =
  "/v1/payments/paymentlink/{customer_id}/{payment_link_id}/checkoutDetail";
const sendPaymentLinkUrl =
  "/v1/payments/customer/{customer_id}/invoice/{invoice_id}/sendPaymentLink";
const syncInvoiceUrl = "/v1/payments/customer/{customer_id}/invoice/{invoice_id}/manualSync";
const toggleEmailOrAutoCollectSettingsUrl =
  "/v1/payments/customer/{customer_id}/invoice/{invoice_id}/toggleEmailAndAutoCollectForInvoice/";
const receiptUrl = "/v1/payments/customer/{customer_id}/transaction/{transaction_id}/receipt/";
const collectNowUrl = "/v1/payments/collectPayment/";
const togglePartialPaymentUrl =
  "/v1/payments/customer/{customer_id}/invoice/{invoice_id}/togglePartialPayment/";
const partialPaymentInfoUrl =
  "/v1/payments/customer/{customer_id}/invoice/{invoice_id}/partial-payment-info/";
const autoCollectDetailsUrl = "/v1/payments/invoice/<invoice_id>/autoCollect/<auto_collect_id>/";
const cancelAutoCollectUrl = "/v1/payments/invoice/cancelAutoCollect/";
const invoicePaymentDetailsUrl = "/v1/payments/invoices/{invoice_id}/payment-details/";
const clientPortalInvoicePaymentDetailsUrl = "/v1/payments/client-portal/customers/{customer_id}/invoices/{invoice_id}/payment-details/";
const unlinkRefundPaymentUrl = "/v1/payments/customer/{customer_id}/invoice/{invoice_id}/unlink-refund-payment/";

interface InvoiceState {
  isLoading: boolean;
  invoices: Invoice[];
  invoicesCount?: number;
  invoiceBaseUrl: string;
  searchValue?: string;
  setInvoiceBaseUrl: (url?: string) => void;
  setSearchValue: (searchValue?: string) => void;
  getInvoices: (
    getAccessTokenSilently: GetAccessTokenFn,
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string,
    query?: string
  ) => Promise<void>;
  getAllInvoices: (
    getAccessTokenSilently: GetAccessTokenFn,
    limit?: number,
    sort?: string,
    filters?: string,
    query?: string
  ) => Promise<Invoice[]>;
  invoicePdfUrl?: string;
  getInvoicePdf: (customerId: string, invoiceId: string) => Promise<string>;
  sendInvoicePaymentLink: (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string
  ) => Promise<void>;
  syncInvoiceNow: (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string
  ) => Promise<void>;
  collectNow: (
    getAccessTokenSilently: GetAccessTokenFn,
    invoiceId: string,
    tokenId: string,
    collectNowAmount?: number,
    sessionCancelToken?: string,
    tabId?: string
  ) => Promise<CollectNowResponse>;
  togglePartialPayment: (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string,
    enabled: boolean
  ) => Promise<Invoice | null>;
  getPartialPaymentInfo: (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string
  ) => Promise<InvoicePartialPaymentInfoView>;
  toggleEmailOrAutoCollectSettings: (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string,
    enabledEmail: boolean,
    enabledAutoCollect: boolean
  ) => Promise<void>;
  checkoutDetailsView?: CheckoutDetailsView;
  getCheckoutDetails: (customerId: string, invoiceId: string, transactionReference?: string) => Promise<void>;
  paymentLinkCheckoutDetailsView?: PaymentLinkCheckoutDetailView;
  getPaymentLinkCheckoutDetails: (
    customerId: string,
    paymentLinkId: string,
    token?: string
  ) => Promise<void>;
  receipt?: Receipt;
  getReceiptDetails: (customerId: string, transactionId: string) => Promise<void>;

  // Client Portal
  clientPortalInvoices: InvoiceForClientPortalView[];
  clientPortalInvoicesCount?: number;
  getClientPortalInvoices: (
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string,
    query?: string
  ) => Promise<void>;
  autoCollectDetails?: AutoCollectDetail;
  getAutoCollectDetails: (invoiceId: string, autoCollectId: string) => Promise<void>;
  cancelAutoCollect: (invoiceId: number, customerId: number) => Promise<void>;
  getInvoicePaymentDetails: (
    getAccessTokenSilently: GetAccessTokenFn,
    invoiceId: string
  ) => Promise<InvoicePaymentView>;
  getClientPortalInvoicePaymentDetails: (customerId: string, invoiceId: string) => Promise<InvoicePaymentView>;
  unlinkRefundPayment: (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string
  ) => Promise<Invoice | null>;
}

export const useInvoiceStore = create<InvoiceState>((set, get) => {
  const { getRequest, postRequest, getRequestNoAuth, postRequestNoAuth } =
    useRequestStore.getState();
  const { addBanner } = useBannerStore.getState();

  const getInvoices = async (
    getAccessTokenSilently: GetAccessTokenFn,
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string,
    query?: string
  ) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<MultiGetResponse<Invoice>>(
        getAccessTokenSilently,
        invoicesUrl,
        {
          limit,
          sort,
          page,
          include_total_count: true,
          filters,
          q: query,
          included_child_entities: ViewGeneratorKey.AdyenTransaction
        }
      );
      set({
        invoices: response.data,
        invoicesCount: response.filtered_count_map?.all,
        isLoading: false
      });
    } catch (error) {
      console.error("Error fetching invoices", error);
      set({ isLoading: false });
    }
  };

  const getAllInvoices = async (
    getAccessTokenSilently: GetAccessTokenFn,
    limit?: number,
    sort?: string,
    filters?: string,
    query?: string
  ) => {
    const fetchInvoices = async (page: number) => {
      const response = await getRequest<MultiGetResponse<Invoice>>(
        getAccessTokenSilently,
        invoicesUrl,
        {
          limit,
          sort,
          page,
          filters,
          q: query,
          included_child_entities: ViewGeneratorKey.AdyenTransaction
        }
      );
      return response.data;
    };

    const count = get().invoicesCount!;
    const pages = Math.ceil(count / limit!);
    const promises = Array.from({ length: pages }, (_, i) => fetchInvoices(i + 1));

    const results = await Promise.all(promises);
    return results.flat();
  };

  const getClientPortalInvoices = async (
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string,
    query?: string
  ) => {
    const clientPortalToken = useClientPortalMetadataStore.getState().clientPortalToken;
    set({ isLoading: true });
    try {
      let params: {
        token: string | undefined;
        limit?: number;
        sort?: string;
        page?: number;
        include_total_count: boolean;
        filters?: string;
        q?: string;
        included_child_entities?: ViewGeneratorKey;
      } = {
        token: clientPortalToken,
        limit,
        sort,
        page,
        include_total_count: true,
        filters,
        included_child_entities: ViewGeneratorKey.AdyenTransaction
      };

      if (query && query.length > 3) {
        params.q = query;
      }
      const response = await getRequestNoAuth<MultiGetResponse<InvoiceForClientPortalView>>(
        invoiceForClientPortalUrl,
        params
      );
      set({
        clientPortalInvoices: response.data,
        clientPortalInvoicesCount: response.filtered_count_map?.all,
        isLoading: false
      });
    } catch (error) {
      console.error("Error fetching client portal invoices", error);
      set({ isLoading: false });
    }
  };

  const getInvoicePdf = async (customerId: string, invoiceId: string) => {
    const response = await getRequestNoAuth<{ pdf_url: string }>(
      invoicePdfViewUrl.replace("{customer_id}", customerId).replace("{invoice_id}", invoiceId)
    );
    set({ invoicePdfUrl: response.pdf_url });
    return response.pdf_url;
  };

  const getCheckoutDetails = async (
    customerId: string,
    invoiceId: string,
    transactionReference?: string
  ) => {
    const params = transactionReference ? { transactionReference } : undefined;
    const response = await getRequestNoAuth<CheckoutDetailsView>(
      getCheckoutDetailsUrl.replace("{customer_id}", customerId).replace("{invoice_id}", invoiceId),
      params
    );
    set({ checkoutDetailsView: response });
    return;
  };

  const getPaymentLinkCheckoutDetails = async (
    customerId: string,
    paymentLinkId: string,
    token?: string
  ) => {
    const params = token ? { token } : undefined;
    const response = await getRequestNoAuth<PaymentLinkCheckoutDetailView>(
      getPaymentLinkCheckoutDetailsUrl
        .replace("{customer_id}", customerId)
        .replace("{payment_link_id}", paymentLinkId),
      params
    );
    set({ paymentLinkCheckoutDetailsView: response });
    return;
  };

  const getReceiptDetails = async (customerId: string, transactionId: string) => {
    const response = await getRequestNoAuth<Receipt>(
      receiptUrl.replace("{customer_id}", customerId).replace("{transaction_id}", transactionId)
    );
    set({ receipt: response });
    return;
  };

  const sendInvoicePaymentLink = async (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string
  ) => {
    try {
      await getRequest(
        getAccessTokenSilently,
        sendPaymentLinkUrl.replace("{customer_id}", customerId).replace("{invoice_id}", invoiceId)
      );
      addBanner({
        type: BannerType.Success,
        message: "Payment link sent successfully."
      });
    } catch (error) {
      console.error("Error sending invoice payment link", error);
    }
  };

  const toggleEmailOrAutoCollectSettings = async (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string,
    enabledEmail: boolean,
    enabledAutoCollect: boolean
  ) => {
    try {
      const response = await postRequest<
        { enabled_email: boolean; enabled_auto_collect: boolean },
        Invoice
      >(
        getAccessTokenSilently,
        toggleEmailOrAutoCollectSettingsUrl
          .replace("{customer_id}", customerId)
          .replace("{invoice_id}", invoiceId),
        {
          enabled_email: enabledEmail,
          enabled_auto_collect: enabledAutoCollect
        }
      );
      const updatedInvoices = [...get().invoices];
      const index = updatedInvoices.findIndex(i => i.id === response.id);
      if (index !== -1) {
        updatedInvoices[index] = response;
        set({ invoices: updatedInvoices });
      }
      addBanner({
        type: BannerType.Success,
        message: "Settings updated successfully."
      });
    } catch (error) {
      console.error("Error updating settings", error);
    }
  };

  const syncInvoiceNow = async (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string
  ) => {
    try {
      const response = await getRequest<{
        result: string;
        invoice_view: Invoice;
      }>(
        getAccessTokenSilently,
        syncInvoiceUrl.replace("{customer_id}", customerId).replace("{invoice_id}", invoiceId)
      );
      const updatedInvoices = [...get().invoices];
      const index = updatedInvoices.findIndex(i => i.id === response.invoice_view.id);
      if (index !== -1) {
        updatedInvoices[index] = response.invoice_view;
        set({ invoices: updatedInvoices });
      }
      addBanner({
        type: BannerType.Info,
        message: response.result
      });
    } catch (error) {
      console.error("Error syncing invoice", error);
    }
  };

  const collectNow = async (
    getAccessTokenSilently: GetAccessTokenFn,
    invoiceId: string,
    tokenId: string,
    collectNowAmount?: number,
    sessionCancelToken?: string,
    tabId?: string
  ) => {
    try {
      return await postRequest<
        { invoice_id: number; token_id: number; collect_now_amount?: number; session_cancel_token?: string; tab_id?: string },
        CollectNowResponse
      >(getAccessTokenSilently, collectNowUrl, {
        invoice_id: parseInt(invoiceId),
        token_id: parseInt(tokenId),
        ...(collectNowAmount !== undefined && Number.isFinite(collectNowAmount) && collectNowAmount > 0 && { collect_now_amount: collectNowAmount }),
        ...(sessionCancelToken !== undefined && { session_cancel_token: sessionCancelToken }),
        ...(tabId !== undefined && { tab_id: tabId })
      });
    } catch (error) {
      console.error("Error collecting payment", error);
      throw error;
    }
  };

  const togglePartialPayment = async (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string,
    enabled: boolean
  ): Promise<Invoice | null> => {
    try {
      const response = await postRequest<{ enabled: boolean }, Invoice | null>(
        getAccessTokenSilently,
        togglePartialPaymentUrl
          .replace("{customer_id}", customerId)
          .replace("{invoice_id}", invoiceId),
        { enabled }
      );
      const updatedInvoices = [...get().invoices];
      const index = updatedInvoices.findIndex(i => i.id === parseInt(invoiceId));
      if (index !== -1 && response) {
        updatedInvoices[index] = response;
        set({ invoices: updatedInvoices });
      }
      addBanner({
        type: BannerType.Success,
        message: enabled
          ? "Partial payment enabled for this invoice."
          : "Partial payment disabled for this invoice."
      });
      return response;
    } catch (error) {
      console.error("Error toggling partial payment", error);
      addBanner({
        type: BannerType.Error,
        message: "Error updating partial payment setting."
      });
      return null;
    }
  };

  const getPartialPaymentInfo = async (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string
  ): Promise<InvoicePartialPaymentInfoView> => {
    return await getRequest<InvoicePartialPaymentInfoView>(
      getAccessTokenSilently,
      partialPaymentInfoUrl
        .replace("{customer_id}", customerId)
        .replace("{invoice_id}", invoiceId)
    );
  };

  const getAutoCollectDetails = async (invoiceId: string, autoCollectId: string) => {
    set({ isLoading: true });
    const response = await getRequestNoAuth<AutoCollectDetail>(
      autoCollectDetailsUrl
        .replace("<invoice_id>", invoiceId)
        .replace("<auto_collect_id>", autoCollectId)
    );
    set({ autoCollectDetails: response, isLoading: false });
    return;
  };

  const cancelAutoCollect = async (invoiceId: number, customerId: number) => {
    set({ isLoading: true });
    try {
      await postRequestNoAuth(cancelAutoCollectUrl, {
        invoice_id: invoiceId,
        customer_id: customerId
      });
      set({ isLoading: false });
    } catch (error) {
      console.error("Error cancelling auto-collect", error);
      addBanner({
        type: BannerType.Error,
        message: "Error cancelling auto-collect."
      });
      set({ isLoading: false });
    }
  };

  const unlinkRefundPayment = async (
    getAccessTokenSilently: GetAccessTokenFn,
    customerId: string,
    invoiceId: string
  ): Promise<Invoice | null> => {
    try {
      const response = await postRequest<Record<string, never>, Invoice | null>(
        getAccessTokenSilently,
        unlinkRefundPaymentUrl.replace("{customer_id}", customerId).replace("{invoice_id}", invoiceId),
        {}
      );
      const updatedInvoices = [...get().invoices];
      const index = updatedInvoices.findIndex(i => i.id === parseInt(invoiceId));
      if (index !== -1 && response) {
        updatedInvoices[index] = response;
        set({ invoices: updatedInvoices });
      }
      addBanner({
        type: BannerType.Success,
        message: "Payment unlinked. Invoice re-opened."
      });
      return response;
    } catch (error: unknown) {
      const axiosMessage =
        typeof error === "object" && error !== null && "response" in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      const message =
        axiosMessage || (error instanceof Error ? error.message : "Failed to unlink payment.");
      addBanner({ type: BannerType.Error, message });
      return null;
    }
  };

  const setInvoiceBaseUrl = (url?: string) => {
    set({ invoiceBaseUrl: url || get().invoiceBaseUrl });
  };

  const setSearchValue = (searchValue?: string) => {
    set({ searchValue });
  };

  const getInvoicePaymentDetails = async (getAccessTokenSilently: GetAccessTokenFn, invoiceId: string) => {
    try {
      const response = await getRequest<InvoicePaymentView>(
        getAccessTokenSilently,
        invoicePaymentDetailsUrl.replace("{invoice_id}", invoiceId)
      );
      return response;
    } catch (error: unknown) {
      const status =
        typeof error === "object" && error !== null && "response" in error
          ? (error as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 404) {
        throw new Error("Payment details feature is not available for your account", { cause: error });
      }
      throw error;
    }
  };

  const getClientPortalInvoicePaymentDetails = async (customerId: string, invoiceId: string) => {
    const clientPortalToken = useClientPortalMetadataStore.getState().clientPortalToken;
    try {
      const response = await getRequestNoAuth<InvoicePaymentView>(
        clientPortalInvoicePaymentDetailsUrl
          .replace("{customer_id}", customerId)
          .replace("{invoice_id}", invoiceId),
        { token: clientPortalToken }
      );
      return response;
    } catch (error: unknown) {
      const status =
        typeof error === "object" && error !== null && "response" in error
          ? (error as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 404) {
        throw new Error("Payment details not available", { cause: error });
      }
      throw error;
    }
  };

  return {
    invoices: [],
    invoiceBaseUrl: "",
    searchValue: undefined,
    setInvoiceBaseUrl,
    setSearchValue,
    getInvoices,
    getAllInvoices,
    clientPortalInvoices: [],
    getClientPortalInvoices,
    getInvoicePdf,
    getCheckoutDetails,
    getPaymentLinkCheckoutDetails,
    getReceiptDetails,
    sendInvoicePaymentLink,
    syncInvoiceNow,
    collectNow,
    toggleEmailOrAutoCollectSettings,
    getAutoCollectDetails,
    cancelAutoCollect,
    getInvoicePaymentDetails,
    getClientPortalInvoicePaymentDetails,
    togglePartialPayment,
    getPartialPaymentInfo,
    unlinkRefundPayment,
    isLoading: true
  };
});
